import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Badge, Button, Spinner } from '@tkpod/ui';
import { useApiClient } from '@tkpod/api-client';

interface UPT {
  id: string;
  name: string;
  slug: string;
  productType: string;
  description: string | null;
  brand?: string | null;
  modelName?: string | null;
  thumbnailUrl?: string | null;
  status: string;
}

interface UptSelectionStepProps {
  selectedUptId: string | null;
  onSelect: (upt: UPT) => void;
  preSelectedUptId?: string | null;
}

const TYPE_META: Record<string, { icon: string; color: string }> = {
  DTF_BY_SIZE: { icon: '🖨️', color: 'info' },
  GANGSHEET: { icon: '📐', color: 'warning' },
  UV_STICKER: { icon: '✨', color: 'success' },
  APPAREL: { icon: '👕', color: 'primary' },
  DRINKWARE: { icon: '☕', color: 'info' },
  ACCESSORIES: { icon: '🎒', color: 'warning' },
};

export function UptSelectionStep({
  selectedUptId,
  onSelect,
  preSelectedUptId,
}: UptSelectionStepProps) {
  const api = useApiClient();
  const autoSelectedRef = useRef(false);

  const uptsQuery = useQuery({
    queryKey: ['catalog', 'templates'],
    queryFn: () =>
      api.get<{ data: UPT[] }>('/api/v1/catalog/templates?status=ACTIVE&limit=50'),
  });

  const upts = uptsQuery.data?.data ?? [];

  useEffect(() => {
    if (autoSelectedRef.current || !preSelectedUptId || upts.length === 0) return;
    const match = upts.find((u) => u.id === preSelectedUptId);
    if (match) {
      autoSelectedRef.current = true;
      onSelect(match);
    }
  }, [preSelectedUptId, upts, onSelect]);

  if (uptsQuery.isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner size="lg" />
      </div>
    );
  }

  if (uptsQuery.isError) {
    return (
      <div className="py-20 text-center">
        <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-500/20">
          <svg className="h-6 w-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
          </svg>
        </div>
        <p className="text-sm text-text-tertiary mb-3">
          Failed to load product templates. Please try again.
        </p>
        <Button variant="outline" size="sm" onClick={() => void uptsQuery.refetch()}>
          Retry
        </Button>
      </div>
    );
  }

  if (upts.length === 0) {
    return (
      <div className="py-20 text-center">
        <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-surface-tertiary">
          <svg className="h-6 w-6 text-text-tertiary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5M10 11.25h4M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" />
          </svg>
        </div>
        <p className="text-sm text-text-tertiary">No active product templates available.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-5">
        <h3 className="text-[15px] font-semibold text-text-primary">
          What would you like to order?
        </h3>
        <p className="mt-1 text-sm text-text-secondary">
          Choose a product type to get started
        </p>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {upts.map((upt) => {
          const isSelected = upt.id === selectedUptId;
          const meta = TYPE_META[upt.productType] ?? { icon: '📦', color: 'default' };

          return (
            <button
              key={upt.id}
              className={`
                group relative w-full rounded-xl border-2 p-4 text-left
                transition-all duration-150
                ${
                  isSelected
                    ? 'border-brand-600 bg-brand-50 shadow-sm'
                    : 'border-border bg-white hover:border-brand-300 hover:shadow-sm'
                }
              `}
              onClick={() => onSelect(upt)}
            >
              {isSelected && (
                <span className="absolute right-3 top-3 flex h-5 w-5 items-center justify-center rounded-full bg-brand-600">
                  <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </span>
              )}

              {upt.thumbnailUrl ? (
                <div className="mb-3 aspect-square w-full overflow-hidden rounded-lg bg-surface-tertiary">
                  <img
                    src={upt.thumbnailUrl}
                    alt={upt.name}
                    className="h-full w-full object-cover"
                  />
                </div>
              ) : (
                <div className="mb-3 flex aspect-square w-full items-center justify-center rounded-lg bg-surface-tertiary">
                  <span className="text-3xl">{meta.icon}</span>
                </div>
              )}
              <div className="min-w-0">
                <h4 className="text-sm font-semibold text-text-primary truncate">
                  {upt.name}
                </h4>
                <Badge
                  variant={meta.color as 'info' | 'warning' | 'success' | 'primary' | 'default'}
                  size="sm"
                  className="mt-1"
                >
                  {upt.productType?.replace(/_/g, ' ') ?? ''}
                </Badge>
                {upt.description && (
                  <p className="mt-2 text-xs text-text-secondary line-clamp-2 leading-relaxed">
                    {upt.description}
                  </p>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
